import React, {useState, useContext} from 'react'
import {useTranslation} from 'react-i18next'

import {Typography} from '~/components/general/typography/Typography'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'
import {SideModal} from '~/components/general/modal/SideModal'
import {Container} from '~/components/general/container/Container'
import {TextArea} from '~/components/general/textArea/TextArea'
import {Button} from '~/components/general/button/Button'
import {useCancelPartnerServiceSlot} from '~/api/partnerAdminSlots'

type CancelAppointmentModalProps = {
  isVisible: boolean
  onClose: () => void
  slotId: string
  partnerServiceId: string
  onSuccess: () => void
}

export const CancelAppointmentModal = ({
  isVisible,
  onClose,
  slotId,
  partnerServiceId,
  onSuccess,
}: CancelAppointmentModalProps) => {
  const {t} = useTranslation()
  const {addNotification} = useContext(NotificationContext)
  const [comment, setComment] = useState('')

  const {mutate, isPending} = useCancelPartnerServiceSlot(
    partnerServiceId,
    slotId,
  )

  const handleConfirm = () => {
    mutate(
      {
        urlParams: {
          partnerServiceId,
          partnerServiceSlotId: slotId,
          comment: comment.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          addNotification(
            t('Appointment cancelled successfully'),
            NotificationType.Success,
          )
          setComment('')
          onSuccess()
        },
        onError: (error: Error) => {
          addNotification(error.message, NotificationType.Error)
        },
      },
    )
  }

  const handleCancel = () => {
    setComment('')
    onClose()
  }

  return (
    <SideModal
      isVisible={isVisible}
      onClose={handleCancel}
      header={
        <Typography size="lg" weight="medium">
          {t('Cancel Appointment')}
        </Typography>
      }>
      <Container direction="column" spacing={2}>
        <Typography weight="bold">{t('Add a comment')}</Typography>
        <TextArea
          height="120px"
          placeholder={t('Write your reason for cancellation (optional)')}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <Typography color="warning" fontStyle="italic">
          {t('This comment will be visible to the patient')}
        </Typography>
        <Container flex direction="row" justify="flex-end" spacing={1}>
          <Button color="secondary" onClick={handleCancel} size="md">
            {t('Back')}
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            size="md"
            disabled={isPending}>
            {t('Confirm')}
          </Button>
        </Container>
      </Container>
    </SideModal>
  )
}
